import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import Seo from "@/components/feature/Seo";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import useHashScroll from "@/hooks/useHashScroll";
import { principles, siteName, stats, testimonials } from "@/mocks/site";
import ContactSection from "@/pages/about/components/ContactSection";

export default function AboutPage() {
  useHashScroll();

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <Seo
        title="关于我们 - 品牌定位·内容原则·联系方式 | 15黑料网"
        description="了解15黑料网的品牌定位、发展理念与内容原则：我们如何选择事件、如何区分事实与推断，以及如何接收读者线索与反馈。"
        keywords="15黑料网关于我们,品牌定位,内容原则,联系方式"
        canonicalPath="/about"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们",
            url: "https://sznita.cn/about",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: siteName,
            url: "https://sznita.cn/",
            email: "editor@15heiliao.com",
            description:
              "专注热点事件深度解读、内幕解析与真相挖掘复盘的内容平台。",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://sznita.cn/" },
              { "@type": "ListItem", position: 2, name: "关于我们", item: "https://sznita.cn/about" },
            ],
          },
        ]}
      />
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1400px] px-4 pb-10 pt-8 md:px-8 md:pb-12 md:pt-10">
            <Breadcrumbs items={[{ label: "关于我们" }]} />
            <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              关于我们
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              15黑料网不是一个追热点的资讯站，我们做的事情更接近「整理」：把一件复杂的事，按时间、角色与疑点拆开，
              让读者自己看清结构，而不是接受一个被情绪包装过的结论。
            </p>
          </div>
        </section>

        <section aria-labelledby="brand-heading" className="w-full bg-background-50">
          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-12 px-4 py-14 md:px-8 md:py-16 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <p className="label-caps text-[11px] text-primary-600">Our Position</p>
              <h2
                id="brand-heading"
                className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl"
              >
                我们相信，看明白比站队更重要
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-600">
                <p>
                  很多事件之所以让人焦虑，不是因为信息太少，而是因为信息太碎。时间被打乱、角色被简化、动机被直接贴上标签，
                  结果是读者看完只觉得更乱。
                </p>
                <p>
                  15黑料网选择的是另一条路：先把能核实的节点按顺序排好，再标出每个角色实际做了什么，然后把还没有解释
                  清楚的疑点单独列出来。我们不急着给结论，因为一个能被检验的问题，往往比一个不可证伪的判断更有价值。
                </p>
                <p>
                  我们相信读者有能力自己判断，只要给到的信息是完整、有序、诚实的。
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 sm:h-80 lg:h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=abstract%20editorial%20flat%20lay%20on%20warm%20cream%20paper%2C%20neatly%20stacked%20documents%2C%20thin%20crimson%20timeline%20marker%20pen%20and%20amber%20magnifier%2C%20calm%20investigative%20magazine%20aesthetic%2C%20soft%20natural%20light%2C%20high%20detail%2C%20no%20text&width=1100&height=1000&seq=about-editorial-15&orientation=landscape"
                  alt="15黑料网编辑部办公桌面与资料整理的抽象插图"
                  title="15黑料网 - 品牌介绍与内容整理方式"
                  className="h-full w-full object-top object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="principles-heading" className="w-full border-y border-background-200 bg-secondary-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-16">
            <p className="label-caps text-[11px] text-primary-600">Principles</p>
            <h2
              id="principles-heading"
              className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl"
            >
              内容原则
            </h2>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">
              原则不是标语，它决定了我们在信息不完整时会怎么做。以下三条，贯穿我们每一篇解读。
            </p>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {principles.map((item, index) => (
                <Reveal key={item.title} delay={index * 70}>
                  <div className="flex h-full flex-col rounded-lg border border-background-300 bg-background-50 p-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 font-heading text-base font-black text-accent-800">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <dl className="mt-12 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-background-300 pt-8 lg:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dt className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                    {item.value}
                  </dt>
                  <dd className="mt-2 text-xs leading-relaxed text-foreground-600">
                    {item.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section aria-labelledby="voice-heading" className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-16">
            <p className="label-caps text-[11px] text-primary-600">Readers' Voice</p>
            <h2
              id="voice-heading"
              className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl"
            >
              读者口碑
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {testimonials.map((item, index) => (
                <Reveal key={item.name} delay={index * 60}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-7">
                    <i className="ri-double-quotes-l text-2xl text-accent-400" aria-hidden="true" />
                    <blockquote className="mt-4 flex-1 font-heading text-base leading-relaxed text-foreground-800">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-3 border-t border-background-200 pt-5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                        {item.name.slice(0, 1)}
                      </span>
                      <span>
                        <span className="block font-heading text-sm font-bold text-foreground-950">
                          {item.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-500">
                          {item.identity}
                        </span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <ContactSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}